import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';

export interface SparkCampaignItem {
  id: number;
  sectorId: number;
  sparkCampaignId?: string | null;
  name: string;
  platform: string;
  templateId: string;
  cadence: number;
  status: string;
  aiAgentId?: number | null;
  aiReplyEnabled: boolean;
  audienceMode: string;
  entrypointId?: string | null;
  executionId?: string | null;
  description?: string | null;
  recipientCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SparkMessageTemplateSummary {
  id: string;
  name: string;
  description?: string | null;
  isOfficial: boolean;
  metaTemplateStatus?: string | null;
  metaTemplateName?: string | null;
  category?: string | null;
  language?: string | null;
  linePhone?: string | null;
  entrypointId?: string | null;
}

export interface SparkMessageTemplateVariable {
  key: string;
  label: string;
  required: boolean;
}

export interface SparkMessageTemplateDetail {
  id: string;
  name: string;
  isOfficial: boolean;
  metaTemplateStatus?: string | null;
  commonVariables: SparkMessageTemplateVariable[];
  items?: SparkMessageTemplateItem[];
  body?: string | null;
  footer?: string | null;
  header?: { format?: string | null; text?: string | null; mediaId?: string | null } | null;
  buttons?: Array<{ type: string; text: string; url?: string | null; phoneNumber?: string | null }>;
}

export interface SparkMessageTemplateItem {
  id: string;
  order: number;
  type: string;
  text?: string | null;
  mediaId?: string | null;
}

export interface SparkCampaignRecipientInput {
  phone: string;
  name?: string | null;
}

export interface SparkExecutionSummary {
  id: string;
  status: string;
  targetCount?: number | null;
  sentCount?: number | null;
  failedCount?: number | null;
  progress?: number | null;
  /** Só nas campanhas executadas pelo Mends (conexão direta com a Meta). */
  deliveredCount?: number | null;
  readCount?: number | null;
  skippedCount?: number | null;
  note?: string | null;
}

export interface CreateSparkCampaignBody {
  name: string;
  description?: string;
  templateId: string;
  platform?: string;
  cadence?: number;
  entrypointId?: string;
  recipients?: SparkCampaignRecipientInput[];
  contactIds?: number[];
  columnIds?: number[];
  commonVariables?: Record<string, string>;
  aiAgentId?: number | null;
  aiReplyEnabled?: boolean;
}

export interface SparkEntrypointOption {
  entrypointId: string;
  label?: string | null;
  linePhone?: string | null;
}

const getAuthHeaders = () => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const base = (sectorId: number) =>
  `${API_URL}/v1/spark-campaigns?sectorId=${sectorId}`;

export async function listSparkCampaigns(sectorId: number): Promise<SparkCampaignItem[]> {
  const response = await axios.get(base(sectorId), { headers: getAuthHeaders() });
  return response.data?.items ?? [];
}

export async function getSparkCampaign(sectorId: number, id: number): Promise<{
  campaign: SparkCampaignItem & { commonVariables?: Record<string, string> };
  recipients: Array<{ id: number; name?: string; phone: string; contactId?: number; enrolledAt?: string; status?: string | null; error?: string | null }>;
  execution?: SparkExecutionSummary | null;
}> {
  const response = await axios.get(`${API_URL}/v1/spark-campaigns/${id}?sectorId=${sectorId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function listSparkTemplates(sectorId: number): Promise<SparkMessageTemplateSummary[]> {
  const response = await axios.get(`${API_URL}/v1/spark-campaigns/templates?sectorId=${sectorId}`, {
    headers: getAuthHeaders(),
  });
  return response.data?.items ?? [];
}

export async function getSparkTemplate(
  sectorId: number,
  templateId: string
): Promise<SparkMessageTemplateDetail> {
  const response = await axios.get(
    `${API_URL}/v1/spark-campaigns/templates/${encodeURIComponent(templateId)}?sectorId=${sectorId}`,
    { headers: getAuthHeaders() }
  );
  const data = response.data || {};
  return {
    id: data.id,
    name: data.name,
    isOfficial: data.isOfficial ?? false,
    metaTemplateStatus: data.metaTemplateStatus ?? null,
    commonVariables: (data.commonVariables ?? []).map((v: SparkMessageTemplateVariable) => ({
      key: v.key,
      label: v.label || v.key,
      required: v.required ?? false,
    })),
    items: (data.items ?? []).map((i: SparkMessageTemplateItem) => ({
      id: i.id ?? '',
      order: i.order ?? 0,
      type: i.type ?? 'text',
      text: i.text ?? null,
      mediaId: i.mediaId ?? null,
    })),
    body: data.body ?? null,
    footer: data.footer ?? null,
    header: data.header ?? null,
    buttons: data.buttons ?? [],
  };
}

export async function previewSparkAudience(
  sectorId: number,
  body: {
    entrypointId?: string;
    platform?: string;
    recipients?: SparkCampaignRecipientInput[];
    contactIds?: number[];
    columnIds?: number[];
  }
): Promise<{ estimatedCount: number; messagingLimit?: number | null; overLimit?: boolean; optedOut?: number }> {
  const response = await axios.post(
    `${API_URL}/v1/spark-campaigns/audience-preview?sectorId=${sectorId}`,
    body,
    { headers: getAuthHeaders() }
  );
  return response.data;
}

export async function listSparkEntrypoints(sectorId: number): Promise<SparkEntrypointOption[]> {
  const response = await axios.get(`${API_URL}/v1/spark-campaigns/entrypoints?sectorId=${sectorId}`, {
    headers: getAuthHeaders(),
  });
  return response.data?.items ?? [];
}

export async function parseSparkCampaignExcel(
  sectorId: number,
  file: File
): Promise<{ recipients: SparkCampaignRecipientInput[]; count: number }> {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');
  const form = new FormData();
  form.append('file', file);
  const response = await axios.post(
    `${API_URL}/v1/spark-campaigns/parse-excel?sectorId=${sectorId}`,
    form,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

export async function createSparkCampaign(
  sectorId: number,
  body: CreateSparkCampaignBody
): Promise<SparkCampaignItem> {
  const response = await axios.post(base(sectorId), body, { headers: getAuthHeaders() });
  return response.data;
}

export { overLimitInfo } from '../utils/campaignOverLimit';

export async function startSparkCampaign(
  sectorId: number,
  id: number,
  commonVariables?: Record<string, string>,
  acceptOverLimit?: boolean
): Promise<SparkCampaignItem> {
  const response = await axios.post(
    `${API_URL}/v1/spark-campaigns/${id}/start?sectorId=${sectorId}`,
    { commonVariables, acceptOverLimit },
    { headers: getAuthHeaders() }
  );
  return response.data;
}

export async function cancelSparkCampaign(sectorId: number, id: number): Promise<SparkCampaignItem> {
  const response = await axios.post(
    `${API_URL}/v1/spark-campaigns/${id}/cancel?sectorId=${sectorId}`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
}

export async function getSparkCampaignExecution(
  sectorId: number,
  id: number
): Promise<{ campaignId: number; execution?: SparkExecutionSummary | null }> {
  const response = await axios.get(`${API_URL}/v1/spark-campaigns/${id}/execution?sectorId=${sectorId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
}
